import React, { useEffect } from 'react';

import { Link } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { formatTitle, getValidRoute } from '../../utils/common';
import { fetchMenu } from '../menu/menuApi';
import LucideIcon from '../../ui-components/LucideIcon';

import { type RootState } from '../../store';
import type { MenuItem } from '../menu/model';

const Sitemap: React.FC = () => {
  const dispatch = useAppDispatch();
  const { filteredMenus, loading, isInitialized } = useAppSelector(
    (state: RootState) => state.menu
  );

  useEffect(() => {
    if (!isInitialized && !loading) {
      dispatch(fetchMenu());
    }
  }, [dispatch, isInitialized]);

  const cardsToRender = loading
    ? Array(6).fill(null)
    : (filteredMenus || []).filter(
        (menu: MenuItem) => !menu.menuName?.toLowerCase().includes('sitemap')
      );

  return (
    <div className="flex flex-col gap-5">
      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cardsToRender.map((menu: MenuItem | null, index: number) => {
          // gather links: either submenus or the item itself if it's a standalone page
          const subItems: { label: string; route: string | null }[] =
            loading || !menu
              ? []
              : menu.submenus && menu.submenus.length > 0
                ? menu.submenus.map((sub) => ({
                    label: formatTitle(sub.menuName),
                    route: getValidRoute(sub.routePath),
                  }))
                : [
                    {
                      label: formatTitle(menu.menuName),
                      route: getValidRoute(menu.routePath),
                    },
                  ];

          return (
            <div
              key={menu ? menu.menuId : index}
              className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden shadow-sm"
            >
              {/* Card Header — neutral gray like the reference */}
              <div className="flex items-center gap-2.5 px-4 py-3 bg-neutral-50 dark:bg-neutral-800/60 border-b border-neutral-200 dark:border-neutral-800">
                {loading ? (
                  <div className="w-5 h-5 bg-neutral-200 dark:bg-neutral-700 animate-pulse rounded" />
                ) : (
                  <LucideIcon
                    iconName={menu?.icon ?? ''}
                    className="text-brand-600 dark:text-brand-400 flex-shrink-0"
                    size={18}
                  />
                )}
                <h2 className="text-sm font-bold text-neutral-800 dark:text-neutral-200">
                  {loading ? (
                    <div className="w-28 h-4 bg-neutral-200 dark:bg-neutral-700 animate-pulse rounded" />
                  ) : (
                    formatTitle(menu?.menuName ?? '')
                  )}
                </h2>
              </div>

              {/* Sub-items */}
              <ul className="px-4 py-3 space-y-2">
                {loading
                  ? Array(3)
                      .fill(0)
                      .map((_, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-neutral-200 dark:bg-neutral-700 flex-shrink-0" />
                          <div className="w-32 h-3.5 bg-neutral-100 dark:bg-neutral-800 animate-pulse rounded" />
                        </li>
                      ))
                  : subItems.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-500 dark:bg-brand-400 flex-shrink-0" />
                        {item.route ? (
                          <Link
                            to={item.route}
                            className="text-sm text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-300 hover:underline transition-colors"
                          >
                            {item.label}
                          </Link>
                        ) : (
                          <span className="text-sm text-neutral-400 dark:text-neutral-600 cursor-not-allowed">
                            {item.label}
                          </span>
                        )}
                      </li>
                    ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Sitemap;
